import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { HttpService } from '../http.service';
import { StereoMeasurementContractService } from '../interfaces/stereo-measurement-contract.service';
import {
  AreaLines3D,
  AreaLinesIV7,
  AreaLinesMeasureData_WithReturnCode,
  AreaLinesNX,
  AreaMeasureData_IV7_WithReturnCode,
  AreaMeasureData_NX_WithReturnCode,
  DepthMeasureData_IV7_WithReturnCode,
  DepthMeasureData_NX_WithReturnCode,
  DepthMeasureData_WithReturnCode,
  DistanceMeasureData_IV7_WithReturnCode,
  DistanceMeasureData_NX_WithReturnCode,
  DistanceMeasureData_WithReturnCode,
  LinesMeasureData_IV7_WithReturnCode,
  LinesMeasureData_NX_WithReturnCode,
  Model3DData,
  PointSet,
  PointSet_IV7,
  PointSet_IV7_WithReturnCode,
  PointSet_NX,
  PointSet_NX_WithReturnCode,
  PointSetWithInfo_WithReturnCode,
  PointToLineMeasureData_IV7_WithReturnCode,
  PointToLineMeasureData_NX_WithReturnCode,
  PointToLineMeasureData_WithReturnCode,
  PolylineData_IV7_WithReturnCode,
  PolylineData_NX_WithReturnCode,
  PolylineData_WithReturnCode,
  StereoReconstructionResult,
  TipDefectResultData,
  Vertices2Df
} from '../../_objects/remeasurement';
import { ElectronService } from '../../electron.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class StereoMeasurementImageOnlineService extends HttpService implements StereoMeasurementContractService {

  constructor(
    private http: HttpClient,
    cookieService: CookieService,
    router: Router
  ) {
    super();
    super.setCookieService(cookieService);
    super.setHttpClient(http);
    super.setRouter(router);
  }

  // SM01 POST /api/v1/stereo/reconstruct/{inspectionId}/{inspectionImageId}
  getAndGenerationOfMeasureInfo(inspectionId: string, inspectionImageId: string): Observable<any> {
    const endpoint = super.getEndpoint('stereo', 'reconstruct', inspectionId, inspectionImageId);

    const header = { withCredentials: super.getWithCredentialsOption() };
    return this.http.post<StereoReconstructionResult>(endpoint, {}, header)
      .pipe(map(response => response as StereoReconstructionResult));
  }

  measureMissingCorner(inspectionId: string, inspectionImageId: string, pointSets: PointSet[], model: Model3DData): Observable<any> {
    const endpoint = super.getEndpoint('stereo', 'missingcorner', inspectionId, inspectionImageId);

    const header = { withCredentials: super.getWithCredentialsOption() };
    return this.http.post<TipDefectResultData>(endpoint, {}, header)
      .pipe(map(response => response as TipDefectResultData));
  }

  // SM02 POST /api/v1/stereo/3dpointset/{inspectionId}/{inspectionImageId}
  measureDotCoordinate3dDataSet(inspectionId: string, inspectionImageId: string, vertices2Df: Vertices2Df, model: Model3DData): Observable<any> {
    const endpoint = super.getEndpoint('stereo', '3dpointset', inspectionId, inspectionImageId);

    return this.http.post<PointSetWithInfo_WithReturnCode>(endpoint, { Vertices: vertices2Df, Model: model },
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as PointSetWithInfo_WithReturnCode));
  }

  // SM03 POST /api/v1/stereo/3dpolyline/{inspectionId}/{inspectionImageId}
  getDistortionReproductionCurve3d(
    inspectionId: string, inspectionImageId: string, vertices2Df: Vertices2Df[], lineType: number, lr: number): Observable<any> {
    const endpoint = super.getEndpoint('stereo', '3dpolyline', inspectionId, inspectionImageId);

    let httpParams = new HttpParams();
    httpParams = httpParams.set('lineType', lineType.toString());
    httpParams = httpParams.set('lr', lr.toString());

    return this.http.post<PolylineData_WithReturnCode>(endpoint, vertices2Df,
      { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map(response => response as PolylineData_WithReturnCode));
  }

  // SM04 POST /api/v1/stereo/3ddistance/{inspectionId}/{inspectionImageId}
  getTwoPointMeasure3d(inspectionId: string, inspectionImageId: string, pointSets: PointSet[]): Observable<any> {
    const endpoint = super.getEndpoint('stereo', '3ddistance', inspectionId, inspectionImageId);

    return this.http.post<DistanceMeasureData_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as DistanceMeasureData_WithReturnCode));
  }

  // SM05 POST /api/v1/stereo/3dpointtoline/{inspectionId}/{inspectionImageId}
  getLineReferenceMeasure3d(inspectionId: string, inspectionImageId: string, pointSets: PointSet[]): Observable<any> {
    const endpoint = super.getEndpoint('stereo', '3dpointtoline', inspectionId, inspectionImageId);

    return this.http.post<PointToLineMeasureData_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as PointToLineMeasureData_WithReturnCode));
  }

  // SM06 POST /api/v1/stereo/3ddepth/{inspectionId}/{inspectionImageId}
  getSurfaceReferenceMeasure3d(inspectionId: string, inspectionImageId: string, pointSets: PointSet[], model: Model3DData): Observable<any> {
    const endpoint = super.getEndpoint('stereo', '3ddepth', inspectionId, inspectionImageId);

    return this.http.post<DepthMeasureData_WithReturnCode>(endpoint, { PointSets: pointSets, Model: model },
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as DepthMeasureData_WithReturnCode));
  }

  // SM07 POST /api/v1/stereo/iv7pointset/{inspectionId}/{inspectionImageId}
  measureDotCoordinateIV7DataSet(inspectionId: string, inspectionImageId: string, pointSets: Vertices2Df): Observable<any> {
    const endpoint = super.getEndpoint('stereo', 'iv7pointset', inspectionId, inspectionImageId);

    return this.http.post<PointSet_IV7_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as PointSet_IV7_WithReturnCode));
  }

  // SM08 POST /api/v1/stereo/iv7polyline/{inspectionId}/{inspectionImageId}
  getDistortionReproductionCurveIV7(inspectionId: string, inspectionImageId: string,
                                    pointSets: Vertices2Df[], lineType: number, lr: number): Observable<any> {
    const endpoint = super.getEndpoint('stereo', 'iv7polyline', inspectionId, inspectionImageId);

    let httpParams = new HttpParams();
    httpParams = httpParams.set('lineType', lineType.toString());
    httpParams = httpParams.set('lr', lr.toString());

    return this.http.post<PolylineData_IV7_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map(response => response as PolylineData_IV7_WithReturnCode));
  }

  // SM09 POST /api/v1/stereo/iv7distance/{inspectionId}/{inspectionImageId}
  getTwoPointMeasureIV7(inspectionId: string, inspectionImageId: string, pointSets: PointSet_IV7[]): Observable<any> {
    const endpoint = super.getEndpoint('stereo', 'iv7distance', inspectionId, inspectionImageId);

    return this.http.post<DistanceMeasureData_IV7_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as DistanceMeasureData_IV7_WithReturnCode));
  }

  // SM10 POST /api/v1/stereo/iv7pointtoline/{inspectionId}/{inspectionImageId}
  getLineReferenceMeasureIV7(inspectionId: string, inspectionImageId: string, pointSets: PointSet_IV7[]): Observable<any> {
    const endpoint = super.getEndpoint('stereo', 'iv7pointtoline', inspectionId, inspectionImageId);

    return this.http.post<PointToLineMeasureData_IV7_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as PointToLineMeasureData_IV7_WithReturnCode));
  }

  // SM11 POST /api/v1/stereo/iv7depth/{inspectionId}/{inspectionImageId}
  getSurfaceReferenceMeasureIV7(inspectionId: string, inspectionImageId: string, pointSets: PointSet_IV7[]): Observable<any> {
    const endpoint = super.getEndpoint('stereo', 'iv7depth', inspectionId, inspectionImageId);

    return this.http.post<DepthMeasureData_IV7_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as DepthMeasureData_IV7_WithReturnCode));
  }

  // SM12 POST /api/v1/stereo/3DareaLines/{inspectionId}/{inspectionImageId}
  getAreaTotalLengthMeasure(inspectionId: string, inspectionImageId: string, pointSets: AreaLines3D): Observable<any> {
    const endpoint = super.getEndpoint('stereo', '3DareaLines', inspectionId, inspectionImageId);

    return this.http.post<AreaLinesMeasureData_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as AreaLinesMeasureData_WithReturnCode));
  }

  // SM13 POST /api/v1/stereo/iv7area/{inspectionId}/{inspectionImageId}
  getAreaMeasure(inspectionId: string, inspectionImageId: string, pointSets: AreaLinesIV7): Observable<any> {
    const endpoint = super.getEndpoint('stereo', 'iv7area', inspectionId, inspectionImageId);

    return this.http.post<AreaMeasureData_IV7_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as AreaMeasureData_IV7_WithReturnCode));
  }

  // SM14 POST /api/v1/stereo/iv7lines/{inspectionId}/{inspectionImageId}
  getOverallLengthMeasure(inspectionId: string, inspectionImageId: string, pointSets: AreaLinesIV7): Observable<any> {
    const endpoint = super.getEndpoint('stereo', 'iv7lines', inspectionId, inspectionImageId);

    return this.http.post<LinesMeasureData_IV7_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as LinesMeasureData_IV7_WithReturnCode));
  }

  // SM16 POST /api/v1/stereo/nxpointset/{inspectionId}/{inspectionImageId}
  measureDotCoordinateNXDataSet(inspectionId: string, inspectionImageId: string, pointSets: Vertices2Df): Observable<any> {
    const endpoint = super.getEndpoint('stereo', 'nxpointset', inspectionId, inspectionImageId);

    return this.http.post<PointSet_NX_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as PointSet_NX_WithReturnCode));
  }

  // SM17 POST /api/v1/stereo/nxpolyline/{inspectionId}/{inspectionImageId}
  getDistortionReproductionCurveNX(inspectionId: string, inspectionImageId: string,
                                   pointSets: Vertices2Df[], lineType: number, lr: number): Observable<any> {
    const endpoint = super.getEndpoint('stereo', 'nxpolyline', inspectionId, inspectionImageId);

    let httpParams = new HttpParams();
    httpParams = httpParams.set('lineType', lineType.toString());
    httpParams = httpParams.set('lr', lr.toString());

    return this.http.post<PolylineData_NX_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map(response => response as PolylineData_NX_WithReturnCode));
  }

  // SM18 POST /api/v1/stereo/nxdistance/{inspectionId}/{inspectionImageId}
  getTwoPointMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: PointSet_NX[]): Observable<any> {
    const endpoint = super.getEndpoint('stereo', 'nxdistance', inspectionId, inspectionImageId);

    return this.http.post<DistanceMeasureData_NX_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as DistanceMeasureData_NX_WithReturnCode));
  }

  // SM19 POST /api/v1/stereo/nxpointtoline/{inspectionId}/{inspectionImageId}
  getLineReferenceMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: PointSet_NX[]): Observable<any> {
    const endpoint = super.getEndpoint('stereo', 'nxpointtoline', inspectionId, inspectionImageId);

    return this.http.post<PointToLineMeasureData_NX_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as PointToLineMeasureData_NX_WithReturnCode));
  }

  // SM20 POST /api/v1/stereo/nxdepth/{inspectionId}/{inspectionImageId}
  getSurfaceReferenceMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: PointSet_NX[]): Observable<any> {
    const endpoint = super.getEndpoint('stereo', 'nxdepth', inspectionId, inspectionImageId);

    return this.http.post<DepthMeasureData_NX_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as DepthMeasureData_NX_WithReturnCode));
  }

  // SM21 POST /api/v1/stereo/nxarea/{inspectionId}/{inspectionImageId}
  getAreaMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: AreaLinesNX): Observable<any> {
    const endpoint = super.getEndpoint('stereo', 'nxarea', inspectionId, inspectionImageId);

    return this.http.post<AreaMeasureData_NX_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as AreaMeasureData_NX_WithReturnCode));
  }

  // SM22 POST /api/v1/stereo/nxlines/{inspectionId}/{inspectionImageId}
  getOverallLengthMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: AreaLinesNX): Observable<any> {
    const endpoint = super.getEndpoint('stereo', 'nxlines', inspectionId, inspectionImageId);

    return this.http.post<LinesMeasureData_NX_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as LinesMeasureData_NX_WithReturnCode));
  }

  // SM15 GET /api/stereo/3dmodel/{inspectionId}/{inspectionImageId}
  get3DModelData(inspectionId: string, inspectionImageId: string): Observable<Model3DData> {
    const endpoint = super.getEndpoint('stereo', '3dmodel', inspectionId, inspectionImageId);
    
    return this.http.get<Model3DData>(endpoint,
      { withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as Model3DData));
  }

  // SM GET /api/image/file
  // 3Dオブジェクトのテクスチャ画像取得
  async getImageBinaryUrl(inspectionId: string, inspectionImageId: string): Promise<string> {
    return super.getEndpoint('stereo', '3dmodel', inspectionId, inspectionImageId, 'file');
  }

  // SM07 POST /api/v1/stereo/iv7pointset/{inspectionId}/{inspectionImageId}
  async measureDotCoordinateIV7DataSetAsync(inspectionId: string, inspectionImageId: string, pointSets: Vertices2Df): Promise<any> {
    const endpoint = super.getEndpoint('stereo', 'iv7pointset', inspectionId, inspectionImageId);

    return this.http.post<PointSet_IV7_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption() })
      .toPromise()
      .then(response => response as PointSet_IV7_WithReturnCode);

  }

  // SM02 POST /api/v1/stereo/3dpointset/{inspectionId}/{inspectionImageId}
  async measureDotCoordinate3dDataSetAsync(inspectionId: string, inspectionImageId: string, vertices2Df: Vertices2Df, model: Model3DData): Promise<any> {
    const endpoint = super.getEndpoint('stereo', '3dpointset', inspectionId, inspectionImageId);

    return this.http.post<PointSetWithInfo_WithReturnCode>(endpoint, { Vertices: vertices2Df, Model: model },
      { withCredentials: super.getWithCredentialsOption() })
      .toPromise()
      .then(response => response as PointSetWithInfo_WithReturnCode);

  }

  // SM16 POST /api/v1/stereo/nxpointset/{inspectionId}/{inspectionImageId}
  async measureDotCoordinateNXDataSetAsync(inspectionId: string, inspectionImageId: string, vertices2Df: Vertices2Df): Promise<any> {
    const endpoint = super.getEndpoint('stereo', 'nxpointset', inspectionId, inspectionImageId);

    return this.http.post<PointSet_NX_WithReturnCode>(endpoint, vertices2Df,
      { withCredentials: super.getWithCredentialsOption() })
      .toPromise()
      .then(response => response as PointSet_NX_WithReturnCode);

  }

  // SM08 POST /api/v1/stereo/iv7polyline/{inspectionId}/{inspectionImageId}
  async getDistortionReproductionCurveIV7Async(inspectionId: string, inspectionImageId: string,
                                               pointSets: Vertices2Df[], lineType: number, lr: number): Promise<any> {
    const endpoint = super.getEndpoint('stereo', 'iv7polyline', inspectionId, inspectionImageId);

    let httpParams = new HttpParams();
    httpParams = httpParams.set('lineType', lineType.toString());
    httpParams = httpParams.set('lr', lr.toString());

    return this.http.post<PolylineData_IV7_WithReturnCode>(endpoint, pointSets,
      { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .toPromise()
      .then(response => response as PolylineData_IV7_WithReturnCode);

  }

  // SM03 POST /api/v1/stereo/3dpolyline/{inspectionId}/{inspectionImageId}
  async getDistortionReproductionCurve3dAsync(
    inspectionId: string, inspectionImageId: string, vertices2Df: Vertices2Df[], lineType: number, lr: number): Promise<any> {
    const endpoint = super.getEndpoint('stereo', '3dpolyline', inspectionId, inspectionImageId);

    let httpParams = new HttpParams();
    httpParams = httpParams.set('lineType', lineType.toString());
    httpParams = httpParams.set('lr', lr.toString());

    return this.http.post<PolylineData_WithReturnCode>(endpoint, vertices2Df,
      { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .toPromise()
      .then(response => response as PolylineData_WithReturnCode);

  }

  // SM17 POST /api/v1/stereo/nxpolyline/{inspectionId}/{inspectionImageId}
  async getDistortionReproductionCurveNXAsync(
    inspectionId: string, inspectionImageId: string, vertices2Df: Vertices2Df[], lineType: number, lr: number): Promise<any> {
    const endpoint = super.getEndpoint('stereo', 'nxpolyline', inspectionId, inspectionImageId);

    let httpParams = new HttpParams();
    httpParams = httpParams.set('lineType', lineType.toString());
    httpParams = httpParams.set('lr', lr.toString());

    return this.http.post<PolylineData_NX_WithReturnCode>(endpoint, vertices2Df,
      { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .toPromise()
      .then(response => response as PolylineData_NX_WithReturnCode);

  }
}
